import BackArrow from 'core/modules/atoms/BackArrow';
import Icon from 'core/modules/atoms/Icon';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import './teacher-layout-wrapper.scss';

const TeacherLayoutWrapper = ({ children, activeNavigationId, onNavigationChange, className = '', contentPadding = 'default', sidebarMode = 'expanded' }) => {
  const { t } = useTranslation(['chatbot']);
  const history = useHistory();
  const [userSidebarCollapsed, setUserSidebarCollapsed] = useState(false);

  // Determine sidebar collapsed state based on mode
  const sidebarCollapsed = sidebarMode === 'collapsed' ? true : sidebarMode === 'expanded' ? false : userSidebarCollapsed; // user-controlled

  // Show collapse button only in user-controlled mode
  const showCollapseButton = sidebarMode === 'user-controlled';

  // Fixed title for the teacher dashboard
  const title = t('teacher_dashboard_title');

  // Fixed navigation items
  const navigationItems = [
    {
      id: 'analytics',
      name: t('analytics_section_diagnostics'),
      icon: 'graphic',
      onClick: () => {
        history.push('/analytics');
      },
    },
    {
      id: 'tools',
      name: t('teacher_tools_section'),
      icon: 'ai',
      onClick: () => {
        history.push('/teacher-tools');
      },
    },
    {
      id: 'history',
      name: t('teacher_tools_history_section'),
      icon: 'replay-history',
      onClick: () => {
        history.push('/teacher-tools/history');
      },
    },
  ];

  const handleClose = () => {
    history.push('/');
  };

  const handleNavigationClick = (item) => {
    if (item.onClick) {
      item.onClick();
    } else if (onNavigationChange) {
      onNavigationChange(item);
    }
  };

  const toggleSidebar = () => {
    setUserSidebarCollapsed(!userSidebarCollapsed);
  };

  const updatedNavigationItems = navigationItems.map((item) => ({
    ...item,
    active: item.id === activeNavigationId,
    onClick: () => handleNavigationClick(item),
  }));

  return (
    <div className={`teacher-layout-wrapper ${sidebarCollapsed ? 'teacher-layout-wrapper--collapsed' : ''} ${className}`}>
      {/* Sidebar */}
      <aside className="teacher-layout-wrapper__sidebar">
        <div className="teacher-layout-wrapper__sidebar-header">
          <BackArrow onClick={handleClose} backIcon={true} />
          {showCollapseButton && (
            <button className="teacher-layout-wrapper__collapse-btn" onClick={toggleSidebar} title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <Icon type={sidebarCollapsed ? 'angle-right' : 'angle-left'} size="medium" />
            </button>
          )}
        </div>

        <div className="teacher-layout-wrapper__title-container">
          <h2 className="teacher-layout-wrapper__title">{title}</h2>
        </div>
        <nav className="teacher-layout-wrapper__navigation">
          {/* Unified Navigation */}
          <ul className="teacher-layout-wrapper__nav-list">
            {updatedNavigationItems.map((item) => (
              <li key={item.id}>
                <button
                  className={`teacher-layout-wrapper__nav-item ${item.active ? 'teacher-layout-wrapper__nav-item--active' : ''}`}
                  onClick={item.onClick}
                  title={item.name}
                >
                  <Icon type={item.icon} size="big" />
                  <span className="teacher-layout-wrapper__nav-text">{item.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main Content */}
      <main className={`teacher-layout-wrapper__main teacher-layout-wrapper__main--${contentPadding}-padding`}>{children}</main>
    </div>
  );
};

TeacherLayoutWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  activeNavigationId: PropTypes.string,
  onNavigationChange: PropTypes.func,
  className: PropTypes.string,
  contentPadding: PropTypes.oneOf(['default', 'left-only', 'none']),
  sidebarMode: PropTypes.oneOf(['expanded', 'collapsed', 'user-controlled']),
};

TeacherLayoutWrapper.defaultProps = {
  className: '',
  contentPadding: 'default',
  sidebarMode: 'expanded',
};

export default TeacherLayoutWrapper;
